import React, { useState } from 'react';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import { formatNumber, getCurrentEventStatus, getDateRange } from 'utils/functions';
import { CalendarIcon, MenuHorizontalIcon } from 'cokitchen-icons';
import { Dropdown, DropdownItem, DropdownList } from '@cokitchen/cokitchen-components';
import AddMoreGuestsModal from 'components/modals/add-more-guests';
import EditEventModal, { UpdateEventType } from 'components/modals/edit-event-modal';
import ActiveTag from 'components/base/active-tag';
import DeleteEventModal from 'components/modals/delete-event-modal';
import WithdrawFundsModal from 'components/modals/withdraw-funds-modal';
import { ENUM_EVENT_STATUS } from 'utils/enum';
import { EventType } from 'models/event.model';

type Props = {
  eventList: EventType[];
  updateEventDetails: (id: string, newData: UpdateEventType) => void;
  handleDeleteEvent: (id: string) => void;
};

const EventCard = ({ eventList, updateEventDetails, handleDeleteEvent }: Props) => {
  const navigate = useNavigate();
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showAddMoreGuestsModal, setShowAddMoreGuestsModal] = useState(false);
  const [activeEvent, setActiveEvent] = useState<EventType>();
  const [showDeleteEventModal, setShowDeleteEventModal] = useState(false);
  const [showWithdrawFundsModal, setShowWithdrawFundsModal] = useState(false);

  return (
    <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6 mt-[42px]'>
      {eventList?.map(({ name, budget, start_date, end_date, event_picture, id }) => {
        const status = getCurrentEventStatus(start_date, end_date);
        return (
          <div
            key={id}
            onClick={() => {
              navigate(`/event-details/${id}`);
            }}
            className='cursor-pointer flex py-3 h-[180px] pr-4 pl-3 justify-between gap-4 event-card'>
            <div className='relative min-h-full rounded border-[#F7F7F8] w-[106px]'>
              <img
                alt='event_picture'
                className='w-full h-full object-cover'
                src={
                  event_picture
                    ? event_picture
                    : 'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/663b1a5a-e6ce-4d3f-b276-9f01ceab5b87:eventscopy%202.png'
                }
              />
            </div>
            <div className='ml-2 basis-2/3 flex flex-col justify-between'>
              <div>
                <div className='flex justify-between w-full'>
                  <p className='text-lg font-[500] capitalize '>{name}</p>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown
                      content={
                        <div className='w-[50px] flex justify-end'>
                          <MenuHorizontalIcon className='w-5 h-5 text-[#6F7376] icon cursor-pointer' />
                        </div>
                      }>
                      <DropdownList>
                        {status !== ENUM_EVENT_STATUS.COMPLETED ? (
                          <>
                            <DropdownItem
                              onClick={() => {
                                setActiveEvent(
                                  eventList.find((event) => event.id === id) || undefined
                                );
                                setShowEditEventModal(true);
                              }}>
                              Edit Event Details
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                setActiveEvent(
                                  eventList.find((event) => event.id === id) || undefined
                                );
                                setShowAddMoreGuestsModal(true);
                              }}>
                              Add More Guests
                            </DropdownItem>
                          </>
                        ) : null}
                        {status === ENUM_EVENT_STATUS.INACTIVE ||
                        status === ENUM_EVENT_STATUS.COMPLETED ? (
                          <>
                            <DropdownItem
                              onClick={() => {
                                setActiveEvent(
                                  eventList.find((event) => event.id === id) || undefined
                                );
                                setShowWithdrawFundsModal(true);
                              }}>
                              Withdraw funds
                            </DropdownItem>
                            <DropdownItem
                              className='text-[#E60A2B]'
                              onClick={() => {
                                setActiveEvent(
                                  eventList.find((event) => event.id === id) || undefined
                                );
                                setShowDeleteEventModal(true);
                              }}>
                              Delete
                            </DropdownItem>
                          </>
                        ) : null}
                      </DropdownList>
                    </Dropdown>
                  </div>
                </div>
                <p className='text-[#96999C] text-sm'>{formatNumber(budget)}</p>
              </div>
              {/* <div className='flex gap-2 border-0 justify-between mt-3 items-center'>
                <input
                  type='range'
                  className={`accent-[#E60A2B] basis-2/3 disabled`}
                  min='0'
                  max='100'
                  value={percentage}
                  style={{
                    backgroundSize: `${percentage}% 100%`
                  }}
                  readOnly
                />
                <p>{percentage}%</p>
              </div> */}
              <ActiveTag status={status} />
              <div className='flex text-[#96999C] gap-1 items-start'>
                <CalendarIcon className='mt-2' />
                <p className='text-[11px] py-2 text-[#96999C]'>
                  {getDateRange(start_date, end_date)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
      {showAddMoreGuestsModal && activeEvent && (
        <AddMoreGuestsModal
          isOpen={showAddMoreGuestsModal}
          onClose={() => setShowAddMoreGuestsModal(false)}
          eventId={activeEvent?.id}
        />
      )}
      {showEditEventModal && activeEvent && (
        <EditEventModal
          isOpen={showEditEventModal}
          onClose={() => {
            setShowEditEventModal(false);
            setActiveEvent(undefined);
          }}
          eventDetails={activeEvent}
          updateEventDetails={updateEventDetails}
        />
      )}
      {showDeleteEventModal && activeEvent && (
        <DeleteEventModal
          isOpen={showDeleteEventModal}
          onClose={() => {
            setShowDeleteEventModal(false);
            setActiveEvent(undefined);
          }}
          event={activeEvent}
          handleDeleteEvent={handleDeleteEvent}
        />
      )}
      {showWithdrawFundsModal && activeEvent && (
        <WithdrawFundsModal
          isOpen={showWithdrawFundsModal}
          onClose={() => {
            setShowWithdrawFundsModal(false);
            setActiveEvent(undefined);
          }}
          event={activeEvent}
        />
      )}
    </div>
  );
};

export default EventCard;
